// ChatMessages.js
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatInputContainer from "./ChatInputContainer"; // <--- import here
import { useThreadApi } from "../../Services/ThreadApiProvider";
import SureLoader from "../SureLoader"
import Button from "../Buttons"
import { ConfirmationProvider } from '../ConfirmationContext';
import { AlertProvider } from '../AlertContext';
import { useConfirmation } from '../ConfirmationContext';
import { useOutletContext } from "react-router-dom";
import rehypeRaw from "rehype-raw";
import TrainPopupModal from "./TrainPopupModal";
import "./TrainPopupModal.css";

import AudioMessagesPlayer from './AudioMessagesPlayer';
import ImageGallery from './ImageGallery';
import { FileAttachment } from "./FileAttachment";

import {
  AssistantHeaderMessage,
  MediaMessageBotURL,
  MediaMessageBotImage,
  MediaMessageBotAudio,
  MediaMessageBotFile,
  MediaMessageBotVideo,
  MediaMessageBotHTML,
  MediaMessageUserURL,
  MediaMessageUserImage,
  MediaMessageUserAudio,
  MediaMessageUserFile,
  MediaMessageUserVideo,
  MediaMessageUserHTML,
} from './MessageComponents';
import MessageMetaBlocks from "./MessageMetaBlock";
import { MessageComposeToolbar } from "./MessageAttachmentsEditor";
import TypingIndicator from "./TypingIndicator";
import PostCallAnalysisCard from "./PostCallAnalysisCard";

const ChatMessagesContent = () => {
  const { organizationId, chatId } = useParams();
  const { canEdit } = useOutletContext();
  const { showConfirmation } = useConfirmation();
  const { isTyping } = useThreadApi();
  const messageRefs = useRef({});
  const [replyLoading, setReplyLoading] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [moderateLoading, setModerateLoading] = useState(false);
  const [virtualLoading, setVirtualLoading] = useState(false);
  const [trainMessageId, setTrainMessageId] = useState(null);
  const [showTrainModal, setShowTrainModal] = useState(false);

  const openTrainModal = (msgId) => {
    setTrainMessageId(msgId);
    setShowTrainModal(true);
  };

  const closeTrainModal = () => {
    setTrainMessageId(null);
    setShowTrainModal(false);
  };

  const onTrainDone = async () => {
    // TODO: Handle apply
    //await applyMessage(trainMessageId, updatedFrame); // mimic `onApplyMessage`
    closeTrainModal();
  };

  const {
    thread,
    threadMessages,
    loadingMessages,
    closeThread,
    setVirtualThread,
    setModeratedThread,
    replyToThreadWithAi,
    deleteMessage,
    applyMessage,
    analysisResults,
    loadingAnalysis
  } = useThreadApi();


  const listKey = `${organizationId || ''}-${chatId || ''}-${thread?.id || ''}`;


  // formatPhoneNumber function for phones of any country:
    const formatPhoneNumber = (phone) => {
        if (!phone) return "";
        const digits = phone.replace(/\D/g, "");
        if (digits.length < 6) return phone; // too short to format

        let countryCode = "";
        let rest = digits;

        if (digits.startsWith("0")) {
            // Local number without country code
            return phone;
        } else if (digits.startsWith("00")) {
            // International prefix with 00
            countryCode = "+" + digits.slice(2, 4);
            rest = digits.slice(4);
        } else if (digits.startsWith("+")) {
            // Already in + format (rare in raw digits form)
            return phone;
        } else if (digits.length > 10) {
            // Assume first 1–3 digits are country code
            countryCode = "+" + digits.slice(0, digits.length - 10);
            rest = digits.slice(digits.length - 10);
        }

        // Format rest as (XXX) XXX XX XX or similar depending on length
        if (rest.length === 10) {
            return `${countryCode} (${rest.slice(0, 3)}) ${rest.slice(3, 6)} ${rest.slice(6, 8)} ${rest.slice(8)}`;
        } else if (rest.length === 9) {
            return `${countryCode} (${rest.slice(0, 2)}) ${rest.slice(2, 5)} ${rest.slice(5, 7)} ${rest.slice(7)}`;
        }

        return `${countryCode} ${rest}`;
    };

  const formatTimestamp = (timestamp) => {
        const now = new Date();
        const msgDate = new Date(timestamp);
        if (isNaN(msgDate.getTime())) return "Invalid date";

        const isToday = now.toDateString() === msgDate.toDateString();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const isYesterday = yesterday.toDateString() === msgDate.toDateString();
        const hours = String(msgDate.getHours()).padStart(2, "0");
        const minutes = String(msgDate.getMinutes()).padStart(2, "0");

        if (isToday) {
            return `${hours}:${minutes}`;
        } else if (isYesterday) {
            return `Yesterday ${hours}:${minutes}`;
        } else {
            const month = msgDate.toLocaleString("default", { month: "short" });
            const day = msgDate.getDate();
            return `${month} ${day}, ${hours}:${minutes}`;
        }
    };

  const onSetVirtualThread = async (virtual) => {
    setVirtualLoading(true);
    try {
      await setVirtualThread(virtual);
    } finally {
      setVirtualLoading(false);
    }
  }

  const onSetModeratedThread = async (moderated) => {
    setModerateLoading(true);
    try {
      await setModeratedThread(moderated);
    } finally {
      setModerateLoading(false);
    }
  }

  const onCloseChat = async () => {
    showConfirmation(
      "Ви впевнені, що хочете закрити чат?",
      async () => {
        setCloseLoading(true);
        try {
          await closeThread();
        } finally {
          setCloseLoading(false);
        }
      }
    );
  }

  const onReplyToThreadWithAi = async () => {
    setReplyLoading(true);
    try {
      await replyToThreadWithAi();
    } finally {
      setReplyLoading(false);
    }
  };

  async function onDeleteMessage(id) {
    await deleteMessage(id);
  }

  // Local input state
  const [messageText, setMessageText] = useState("");
  const [editedTexts, setEditedTexts] = useState({});
  const [editedImagesByMessage, setEditedImagesByMessage] = useState({});
  const [editedFilesByMessage, setEditedFilesByMessage] = useState({});
  const [editedAudioByMessage, setEditedAudioByMessage] = useState({});

  const [draftNewUploadsByMessage, setDraftNewUploadsByMessage] = useState({});

  async function onApplyMessage(id, text, attachments, newUploadsPayload) {
    await applyMessage(id, text, attachments, newUploadsPayload);
  }

  function handleImagesChange(messageId, newVisibleKeys) {
    setEditedImagesByMessage(prev => ({
      ...prev,
      [messageId]: newVisibleKeys,
    }));
  }

  function handleRemoveFile(messageId, fileKeyToRemove) {
    setEditedFilesByMessage((prev) => {
      const prevFiles = prev[messageId] ?? (threadMessages.find(m => m.id === messageId)?.files || []);
      const newFiles = prevFiles.filter(f => f.key !== fileKeyToRemove);

      return {
        ...prev,
        [messageId]: newFiles,
      };
    });
  }

  function handleRemoveAudio(messageId, audioKeyToRemove) {
    setEditedAudioByMessage((prev) => {
      const originalAudios = threadMessages.find(m => m.id === messageId)?.audio || [];

      const prevAudios = prev[messageId] ?? originalAudios;

      const newAudios = prevAudios.filter(a => a.key !== audioKeyToRemove);

      return {
        ...prev,
        [messageId]: newAudios,
      };
    });
  }

  function addEmojiBeforeClosingBold(text, emoji) {
    if (/\*\*\s*$/.test(text)) {
      return text.replace(/\s*\*\*\s*$/, `${emoji}**`);
    }
    return `${text}${emoji}`;
  }

  const handleAddEmojiToDraft = (messageId, emojiStr) => {
    setEditedTexts((prev) => {
      const oldVal = prev[messageId] !== undefined
        ? prev[messageId]
        : (() => {
          const msg = threadMessages.find(m => m.id === messageId);
          if (!msg) return "";
          return typeof msg.text === "string" ? msg.text : JSON.stringify(msg.text, null, 2);
        })();

      return {
        ...prev,
        [messageId]: addEmojiBeforeClosingBold(oldVal || "", emojiStr),
      };
    });
  };

  const handleAddFilesToDraft = (messageId, newFilesArr) => {
    setDraftNewUploadsByMessage((prev) => {
      const oldArr = prev[messageId] || [];
      return {
        ...prev,
        [messageId]: [...oldArr, ...newFilesArr],
      };
    });
  };

  // For autoscrolling to bottom
  const messagesEndRef = useRef(null);

  // Scroll to bottom whenever new messages or the typing indicator changes
  useEffect(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({behavior: "auto"});
    });
  }, [threadMessages, isTyping]);

  useEffect(() => {
    setMessageText("");
  }, [thread]);

  return (
    <div className="chat-container">
      {/* Header area, if desired */}
      <div className={`chat-header ${thread?.is_active === true ? "closed-chat" : ""}`}>
        <div className="chat-info">
          {thread && (
            <>
              <div
                style={thread?.starterImg ? { backgroundImage: `url(${thread.starterImg})` } : {}}
                className={`chat-logo ${thread?.starterImg ? "" : "user-logo"}`}
              ></div>
              <div className="chat-creds">
                <h4>{thread.name}</h4>
                <div className="phone-nuber">{formatPhoneNumber(thread.phone)}</div>

                <span>{thread.email}</span>
              </div>
            </>
          )}
        </div>
        {thread && thread.is_active === true && canEdit && (
          <div className="chat-controls">
            {thread.is_virtual === true ? <div className="assistant-logo"></div> : ""}
            {thread.is_virtual === false && (
              <Button
                variant="ghost"
                size="medium"
                onClick={onReplyToThreadWithAi}
                disabled={replyLoading}
                className={replyLoading ? "loader" : ""}
              >
                Включити ШІ + Відповісти
              </Button>
            )}
            {thread.is_virtual === false && (
              <Button
                variant="ghost"
                size="medium"
                onClick={() => onSetVirtualThread(true)}
                disabled={virtualLoading}
                className={virtualLoading ? "loader" : ""}
              >
                Включити ШІ
              </Button>
            )}
            {thread.is_virtual === true && thread.is_moderated === false && (
              <Button
                variant="ghost"
                size="medium"
                onClick={() => onSetModeratedThread(true)}
                disabled={moderateLoading}
                className={moderateLoading ? "loader" : ""}
              >
                Включити Модерацію
              </Button>
            )}
            {thread.is_virtual === true && thread.is_moderated === true && (
              <Button
                variant="ghost"
                size="medium"
                onClick={() => onSetModeratedThread(false)}
                disabled={moderateLoading}
                className={moderateLoading ? "loader" : ""}
              >
                Вимкнути Модерацію
              </Button>
            )}
            {thread.is_virtual === true && (
              <Button
                variant="ghost"
                size="medium"
                onClick={() => onSetVirtualThread(false)}
                disabled={virtualLoading}
                className={virtualLoading ? "loader" : ""}
              >
                Перехопити чат
              </Button>
            )}
            <Button
              variant="danger"
              size="medium"
              onClick={onCloseChat}
              disabled={closeLoading}
              className={closeLoading ? "loader" : ""}
            >
              Завершити чат
            </Button>
          </div>
        )}
        {thread && thread.is_active === false && (<div className="closed-chat-message">Чат закритий</div>)}
      </div>

      {/* Post-call analysis results */}
      {thread && !thread.is_active && !loadingAnalysis && analysisResults && (
        <PostCallAnalysisCard analysisResult={analysisResults} />
      )}

      {/* Main messages area with fancy rendering */}
      <div key={listKey} className="chat-messages">
        <AnimatePresence key={listKey} initial={false}>
          {loadingMessages ? (
            <SureLoader/>
          ) : (
            threadMessages.map((msg, index) => {
              // ────────────────
              // CASE 1: Assistant Header Message (intro)
              // ────────────────
              if (msg.newassistant) {
                return (
                  <React.Fragment key={msg.id}>
                    <AssistantHeaderMessage message={msg} />
                  </React.Fragment>
                );
              }

              // ────────────────
              // Compute grouping information for all messages
              // ────────────────
              const isAssistant = msg.sender === 'assistant';

              // Check previous and next messages
              const prevMsg = threadMessages[index - 1];
              const nextMsg = threadMessages[index + 1];

              const isFirstAssistantMsg = isAssistant && (!prevMsg || prevMsg.sender !== 'assistant');
              const isLastAssistantMsg = isAssistant && (!nextMsg || nextMsg.sender !== 'assistant');
              const isMiddleAssistantMsg = isAssistant && !isFirstAssistantMsg && !isLastAssistantMsg;
              const isOnlyAssistantMsg = isFirstAssistantMsg && isLastAssistantMsg;

              const isUser = msg.sender === 'user';
              const isFirstUserMsg = isUser && (!prevMsg || prevMsg.sender !== 'user');
              const isLastUserMsg = isUser && (!nextMsg || nextMsg.sender !== 'user');
              const isMiddleUserMsg = isUser && !isFirstUserMsg && !isLastUserMsg;
              const isOnlyUserMsg = isFirstUserMsg && isLastUserMsg;


              const groupingClasses = [
                isAssistant ? 'assistant' : 'user',

                // Assistant message grouping
                isOnlyAssistantMsg ? 'only-assistant-msg' :
                  isFirstAssistantMsg ? 'first-assistant-msg' :
                    isMiddleAssistantMsg ? 'middle-assistant-msg' :
                      isLastAssistantMsg ? 'last-assistant-msg' : '',

                // User message grouping
                isOnlyUserMsg ? 'only-user-msg' :
                  isFirstUserMsg ? 'first-user-msg' :
                    isMiddleUserMsg ? 'middle-user-msg' :
                      isLastUserMsg ? 'last-user-msg' : '',
              ]
                .filter(Boolean)
                .join(' ');

              // ────────────────
              // Determine inner content:
              // If msg.typeOfMedia exists and is not "simple_text", select the proper media component;
              // otherwise, use the simple text markup.
              // ────────────────
              const meta = msg.message_metadata || msg.assistant_metadata || {};
              const thematics = Array.isArray(msg.thematics) ? msg.thematics : [];
              const triggers = Array.isArray(msg.triggers) ? msg.triggers : [];
              const guardrails = Array.isArray(msg.guardrails) ? msg.guardrails : [];
              const webs = Array.isArray(meta.url_annotations) ? meta.url_annotations : [];
              const filesMeta = Array.isArray(meta.file_annotations) ? meta.file_annotations : [];
              const frames = Array.isArray(meta.frames) ? meta.frames : [];
              const notificationsList = Array.isArray(meta.notifications) ? meta.notifications : [];
              const connectionObjectsList = Array.isArray(meta.connection_objects) ? meta.connection_objects : [];
              const fileSearchQueries = Array.isArray(meta.file_search_queries) ? meta.file_search_queries : [];
              const reasoningSteps = Array.isArray(meta.reasoning_steps) ? meta.reasoning_steps : [];

              const audioItem = msg.audio?.[0] || null;
              const fileItems = Array.isArray(msg.files) ? msg.files : [];

              let innerContent;

              if (msg.typeOfMedia && msg.typeOfMedia !== "simple_text") {
                if (msg.sender === 'user') {
                  switch (msg.typeOfMedia) {
                    case 'url':
                      innerContent = <MediaMessageBotURL message={msg} />;
                      break;
                    case 'image':
                      innerContent = <MediaMessageBotImage message={msg} />;
                      break;
                    case 'audio':
                      innerContent = <MediaMessageBotAudio message={msg} />;
                      break;
                    case 'file':
                      innerContent = <MediaMessageBotFile message={msg} />;
                      break;
                    case 'video':
                      innerContent = <MediaMessageBotVideo message={msg} />;
                      break;
                    case 'HTML':
                      innerContent = <MediaMessageBotHTML message={msg} />;
                      break;
                    default:
                      innerContent = null;
                  }
                } else if (msg.sender === 'assistant') {
                  switch (msg.typeOfMedia) {
                    case 'url':
                      innerContent = <MediaMessageUserURL message={msg} />;
                      break;
                    case 'image':
                      innerContent = <MediaMessageUserImage message={msg} />;
                      break;
                    case 'audio':
                      innerContent = <MediaMessageUserAudio message={msg} />;
                      break;
                    case 'file':
                      innerContent = <MediaMessageUserFile message={msg} />;
                      break;
                    case 'video':
                      innerContent = <MediaMessageUserVideo message={msg} />;
                      break;
                    case 'HTML':
                      innerContent = <MediaMessageUserHTML message={msg} />;
                      break;
                    default:
                      innerContent = null;
                  }
                }
              } else {
                // Simple text message markup
                let formattedText = typeof msg.text === 'string' ? msg.text : JSON.stringify(msg.text, null, 2);


                innerContent = (
                  <>
                    {(msg.images && msg.images.length > 0) && (
                      <ImageGallery message={msg} />
                    )}
                    {(audioItem) && (
                      <AudioMessagesPlayer
                        audioKey={audioItem.key}
                        histogramData={audioItem.histogram || []}
                        playbackTime={audioItem.playback_time}
                      />
                    )}
                    {(fileItems && fileItems.length > 0) && fileItems.map((file, i) => (
                      <FileAttachment key={file.key || i} file={file} />
                    ))}
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        a: ({node, ...props}) => (
                          <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                          </a>
                        )
                      }}
                    >
                      {formattedText}
                    </ReactMarkdown>
                    <MessageMetaBlocks
                      organizationId={organizationId}
                      thematics={thematics}
                      triggers={triggers}
                      guardrails={guardrails}
                      webs={webs}
                      filesMeta={filesMeta}
                      frames={frames}
                      notificationsList={notificationsList}
                      fileSearchQueries={fileSearchQueries}
                      connectioObjectsList={connectionObjectsList}
                      reasoningSteps={reasoningSteps}
                    />
                  </>
                );
              }

              return (
                <div
                  layout="position"
                  key={msg.id || index}
                  className={`message-row ${groupingClasses} ${((isUser && !isLastUserMsg) || (isAssistant && !isLastAssistantMsg)) ? "no-badge" : ""}`}
                >
                  {/* Avatar for user messages (shown only for the last message in a sequence) */}
                  {!isAssistant && isLastUserMsg && (
                    <motion.div
                      className={`message-avatar ${thread?.starterImg ? "" : "user-logo"}`}
                      style={thread?.starterImg ? { backgroundImage: `url(${thread.starterImg})` } : {}}
                      initial={{
                        // opacity: 0,
                        scale: 0.5 }}
                      animate={{
                        // opacity: 1,
                        scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{
                        type: "spring",
                        stiffness: 200,
                        damping: 15,
                        duration: 0.1,
                      }}
                    />
                  )}

                  {/* The bubble */}
                  <motion.div
                    className={`message-bubble ${isAssistant ? 'assistant-msg' : 'user-msg'}
                        ${msg.type === 'bot' ? 'bot' : 'human'}
                        ${msg.isSent === false ? 'moderated' : ''}`
                    }
                    initial={{
                      // opacity: 0,
                      scale: 0.9,
                      x: isAssistant ? -10 : 10
                    }}
                    animate={{
                      // opacity: 1,
                      scale: 1,
                      x: 0
                    }}
                    exit={{opacity: 0, scale: 0.9, x: isAssistant ? -10 : 10}}
                    transition={{
                      type: 'spring', stiffness: 200, damping: 15, duration: 0.1,
                      // delay: index * 0.02
                    }}
                  >
                    {/* {innerContent} */}
                    {msg.isSent === false ? (
                      <>
                        <span style={{fontSize: "12px"}}>
                          {formatTimestamp(msg.timestamp)}
                        </span>
                        {isAssistant && meta.run_key && (
                          <span style={{fontSize: "12px", marginLeft: "8px"}}>{meta.run_key}</span>
                        )}

                        {(msg.images && msg.images.length > 0) && (
                          <ImageGallery
                            message={msg}
                            isEditable={msg.isSent === false}
                            onImagesChange={handleImagesChange}
                          />
                        )}

                        {(() => {
                          const currentAudios =
                            editedAudioByMessage[msg.id] ??
                            (Array.isArray(msg.audio) ? msg.audio : []);

                          if (!currentAudios || currentAudios.length === 0) return null;

                          return currentAudios.map((audio, idx) => (
                            <AudioMessagesPlayer
                              key={audio.key || idx}
                              audioKey={audio.key}
                              histogramData={audio.histogram || []}
                              playbackTime={audio.playback_time}
                              onDelete={(key) => handleRemoveAudio(msg.id, key)}
                            />
                          ));
                        })()}

                        {(() => {
                          const currentFiles =
                            editedFilesByMessage[msg.id] ??
                            (Array.isArray(msg.files) ? msg.files : []);

                          if (!currentFiles || currentFiles.length === 0) return null;

                          return currentFiles.map((file, i) => (
                            <FileAttachment
                              key={file.key || i}
                              file={file}
                              onDelete={(key) => handleRemoveFile(msg.id, key)}
                            />
                          ));
                        })()}
                        <textarea
                          data-id={msg.id}
                          className="message-edit-textarea"
                          value={
                            editedTexts[msg.id] !== undefined
                              ? editedTexts[msg.id]
                              : typeof msg.text === 'string'
                                ? msg.text
                                : JSON.stringify(msg.text, null, 2)
                          }
                          style={{overflow: 'hidden', resize: 'none'}}
                          ref={(el) => {
                            if (el) {
                              el.style.height = 'auto';
                              el.style.height = `${el.scrollHeight}px`;
                              messageRefs.current[msg.id] = el;
                            }
                          }}
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                            setEditedTexts((prev) => ({...prev, [msg.id]: e.target.value}));
                          }}
                        />
                        <MessageMetaBlocks
                          organizationId={organizationId}
                          thematics={thematics}
                          triggers={triggers}
                          guardrails={guardrails}
                          webs={webs}
                          filesMeta={filesMeta}
                          frames={frames}
                          notificationsList={notificationsList}
                          fileSearchQueries={fileSearchQueries}
                          connectioObjectsList={connectionObjectsList}
                        />

                        <div className="draft-compose-toolbar-row">
                          <MessageComposeToolbar
                            onEmoji={(emojiStr) => handleAddEmojiToDraft(msg.id, emojiStr)}
                            onAddFiles={(newFilesArr) => handleAddFilesToDraft(msg.id, newFilesArr)}
                            wrapperClassName="chat-options"
                            emojiButtonClassName="emozy-button"
                            imageButtonClassName="attach-file-button image-uploader"
                            fileButtonClassName="attach-file-button"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <span style={{fontSize: "12px"}}>
                          {formatTimestamp(msg.timestamp)}
                        </span>
                        {isAssistant && meta.run_key && (
                          <span style={{fontSize: "12px", marginLeft: "8px"}}>{meta.run_key}</span>
                        )}


                        {innerContent}
                      </>
                    )}
                    {msg.isSent === false && (
                      <div className="buttons-panel">
                        <Button
                          variant="danger" size='small' className="remove" onClick={() => onDeleteMessage(msg.id)}>
                          Delete
                        </Button>

                        <Button
                          variant="ghost"
                          size='small'
                          className="apply"
                          onClick={async () => {
                            const value =
                              messageRefs.current[msg.id]?.value ?? msg.text;

                            const finalImages =
                              editedImagesByMessage[msg.id] ??
                              (Array.isArray(msg.images) ? msg.images : []);

                            const currentFiles =
                              editedFilesByMessage[msg.id] ??
                              (Array.isArray(msg.files) ? msg.files : []);

                            const finalFileKeys = currentFiles
                              .map(f => f.key)

                            const finalAudios =
                              editedAudioByMessage[msg.id] ??
                              (Array.isArray(msg.audio) ? msg.audio : []);

                            const extraDraftUploads =
                              draftNewUploadsByMessage[msg.id] || [];

                            const attachmentsPayload = {
                              images: finalImages,
                              files: finalFileKeys,
                              audios: finalAudios,
                            };

                            const newUploadsPayload = extraDraftUploads.map(f => ({
                              file: f.file,
                              name: f.name,
                              extension: f.extension,
                              sizeMB: f.sizeMB,
                              base64: f.base64Preview || f.base64File || null,
                              type: f.base64Preview ? "image" : "file",
                            }));

                            await onApplyMessage(
                              msg.id,
                              value,
                              attachmentsPayload,
                              newUploadsPayload
                            );

                            setDraftNewUploadsByMessage(prev => {
                              const copy = { ...prev };
                              delete copy[msg.id];
                              return copy;
                            });
                          }}
                        >
                          Apply
                        </Button>
                        {msg.tags && msg.tags.length > 0 && msg.tags.some(t => t === 'FRAME') && (
                          <Button variant="ghost" size='small' className="modify"
                                  disabled={showTrainModal}
                                  onClick={() => openTrainModal(msg.id)}
                          >
                            Train
                          </Button>
                        )}

                        {showTrainModal && trainMessageId === msg.id && (
                          <TrainPopupModal
                            onClose={closeTrainModal}
                            onTrainDone={onTrainDone}
                            messageId={msg.id}
                          />
                        )}

                      </div>
                    )}
                  </motion.div>

                </div>
              );
            })
          )}

        </AnimatePresence>
         {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>


      {thread && thread.is_active === true && (
        <ChatInputContainer content={messageText}
      />)}
    </div>
  );
};

function ChatMessages() {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <ChatMessagesContent/>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default ChatMessages;
